import type { NurturingJourneyStagesApiBody } from 'app/pages/TestNurturingPage/apiService/testNurturingApiService';
import { titleCaseSnakeCasing } from 'utils/text';

export type TestNurturingAutomationStep = 1 | 2 | 3;

/**
 * Extracts the stages list from the nurturing-journey/stages API response.
 *
 * Prefers structured `stages: Array<{ code, display_name }>`; falls back to
 * legacy `nurturing_stages: string[]` for backward compatibility.
 */
export function getNurturingStagesArrayFromApiResponse(res: unknown): unknown[] {
  if (!res) return [];
  const root = res as {
    body?: NurturingJourneyStagesApiBody;
    data?: NurturingJourneyStagesApiBody;
  };
  const body =
    root.body ??
    root.data ??
    (res as NurturingJourneyStagesApiBody | null);

  if (body && typeof body === 'object') {
    if (Array.isArray(body.stages) && body.stages.length > 0) {
      return body.stages;
    }
    if (Array.isArray(body.nurturing_stages)) {
      return body.nurturing_stages;
    }
  }

  if (res && typeof res === 'object') {
    const flat = res as {
      stages?: unknown;
      nurturing_stages?: unknown;
    };
    if (Array.isArray(flat.stages) && flat.stages.length > 0) {
      return flat.stages;
    }
    if (Array.isArray(flat.nurturing_stages)) {
      return flat.nurturing_stages;
    }
  }
  return [];
}

/** Builds a lookup map from stage `code` → `display_name` (case-insensitive keys). */
export function buildNurturingStageCodeToDisplayNameMap(
  rows: unknown[],
): Map<string, string> {
  const map = new Map<string, string>();
  rows.forEach((row, index) => {
    const code = getNurturingStageOptionValue(row, index);
    if (!code || code.startsWith('__idx_')) return;
    const label = getNurturingStageOptionLabel(row, index);
    map.set(code, label);
    map.set(code.toLowerCase(), label);
  });
  return map;
}

/** Resolves a history/API `nurturing_stage` code to a user-facing label. */
export function getNurturingStageDisplayName(
  stageCode: string | null | undefined,
  labelMap: Map<string, string>,
): string {
  const trimmed = stageCode == null ? '' : String(stageCode).trim();
  if (!trimmed) return '';
  const fromMap =
    labelMap.get(trimmed) ?? labelMap.get(trimmed.toLowerCase());
  if (fromMap) return fromMap;
  return titleCaseSnakeCasing(trimmed);
}

/**
 * Returns the value sent back to the API for a selected nurturing-stage row.
 *
 * Handles three response shapes from `api/v2/mia/nurturing-journey/stages`:
 *  - new structured option: `{ code, display_name }` → use `code`
 *  - legacy primitive: `"retargeting"` → use the string as-is
 *  - older richer shape: `{ stage_key | key | _id | id }` → first matching key
 */
export function getNurturingStageOptionValue(row: unknown, index: number): string {
  if (typeof row === 'string' || typeof row === 'number' || typeof row === 'boolean') {
    return String(row);
  }
  if (row && typeof row === 'object') {
    const r = row as Record<string, unknown>;
    const v = r.code ?? r.value ?? r.stage_key ?? r.key ?? r._id ?? r.id;
    if (v != null && String(v).length > 0) return String(v);
  }
  return `__idx_${index}`;
}

/**
 * Returns the user-facing label for a nurturing-stage row.
 *
 * Prefers the structured `display_name` from the new API shape; falls back to
 * legacy keys (`name`, `stage_name`, `label`, `title`, `stage_key`, `key`,
 * `code`, `value`) so the dropdown stays readable even if the backend rolls back.
 */
export function getNurturingStageOptionLabel(row: unknown, index: number): string {
  if (typeof row === 'string' || typeof row === 'number' || typeof row === 'boolean') {
    return String(row);
  }
  if (row && typeof row === 'object') {
    const r = row as Record<string, unknown>;
    const label =
      r.display_name ??
      r.name ??
      r.stage_name ??
      r.label ??
      r.title ??
      r.stage_key ??
      r.key ??
      r.code ??
      r.value;
    if (label != null && String(label).length > 0) return String(label);
  }
  return `Stage ${index + 1}`;
}

export function getUnsoldLeadRowId(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  const r = row as Record<string, unknown>;
  return String(r._id ?? r.id ?? r.lead_id ?? '');
}

export function getUnsoldLeadDisplayName(row: unknown): string {
  if (!row || typeof row !== 'object') return '—';
  const r = row as Record<string, unknown>;
  return String(r.name ?? r.full_name ?? '—');
}

export function getUnsoldLeadDisplaySegment(row: unknown): string {
  if (!row || typeof row !== 'object') return '—';
  const r = row as Record<string, unknown>;
  return String(
    r.maiq_segment ??
      r.segment_name ??
      r.primary_segment ??
      r.segment ??
      r.voi_model ??
      r.voi_name ??
      '—',
  );
}

/** Raw segment label on lead row (for quick-filter options); prefers `maiq_segment`. */
export function getUnsoldLeadSegmentNameRaw(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  const r = row as Record<string, unknown>;
  const v = r.maiq_segment ?? r.segment_name;
  if (typeof v === 'string') return v.trim();
  if (v == null) return '';
  return String(v).trim();
}
